/* eslint-disable space-before-function-paren */
import { normalizeUser } from 'src/data/User/user-normalizers';
import {
    LOG_EVENT_TYPE,
    createLogEvent,
} from 'src/logger/log-event';
import { LOG_LEVEL, logger } from 'src/logger/logger';
import { getRequestFailureDetails } from 'src/logger/request-failure-details';
import { get, put } from 'src/services/api';
import { buildUrl, getUserEmail } from 'src/services/requests/request.utils';

const CODE_LOCATION = 'src/services/requests/user/user.js';
const USER_PROFILE_ENDPOINT = '/users/email';
const USER_PROFILE_REQUEST_DATA = Object.freeze({
    method: 'GET',
    endpoint: USER_PROFILE_ENDPOINT,
});

const selectUserProfileResponseData = (rawResponse, statusCode) => {
    const agencyId = rawResponse?.inAgency
        ?? rawResponse?.agencies_id
        ?? rawResponse?.in_agency;
    const responseData = {
        status_code: statusCode,
        jurisdiction_count: Array.isArray(rawResponse?.jurisdictions)
            ? rawResponse.jurisdictions.length
            : 0,
        has_role_permissions: Boolean(
            rawResponse?.role?.roleParse
            && Object.keys(rawResponse.role.roleParse).length > 0
        ),
    };

    [
        'lyt_auth_role',
        'usertype',
        'account_approved',
    ].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(rawResponse || {}, field)) {
            responseData[field] = rawResponse[field];
        }
    });

    if (agencyId !== undefined && agencyId !== null && agencyId !== '') {
        responseData.agency_id = agencyId;
    }

    return responseData;
};

const getServiceRejectionOutcome = (failureCategory) => {
    if (failureCategory === 'canceled') {
        return 'canceled';
    }

    if (failureCategory === 'http') {
        return 'response_received';
    }

    if (failureCategory === 'request') {
        return 'request_rejected';
    }

    return 'no_response';
};

const createUserProfileServiceRequestStartedEvent = ({
    transactionId,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.SERVICE,
    codeLocation: CODE_LOCATION,
    methodName: 'getUserPersonalInformationRequest',
    level: LOG_LEVEL.INFO,
    message: 'User profile service request started',
    outcome: 'request_started',
    transactionId,
    attributes: {
        request_data: USER_PROFILE_REQUEST_DATA,
    },
});

const createUserProfileServiceResponseEvent = ({
    transactionId,
    rawResponse,
    statusCode,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.SERVICE,
    codeLocation: CODE_LOCATION,
    methodName: 'getUserPersonalInformationRequest',
    level: LOG_LEVEL.INFO,
    message: 'User profile service response received',
    outcome: 'response_received',
    transactionId,
    attributes: {
        request_data: USER_PROFILE_REQUEST_DATA,
        response_data: selectUserProfileResponseData(
            rawResponse,
            statusCode,
        ),
    },
});

const createUserProfileServiceRejectionEvent = ({
    transactionId,
    error,
}) => {
    const failureDetails = getRequestFailureDetails(error);
    const outcome = getServiceRejectionOutcome(
        failureDetails.failure_category,
    );

    return createLogEvent({
        eventType: LOG_EVENT_TYPE.SERVICE,
        codeLocation: CODE_LOCATION,
        methodName: 'getUserPersonalInformationRequest',
        level: LOG_LEVEL.INFO,
        message: outcome === 'response_received'
            ? 'User profile service response received'
            : 'User profile service request did not return a response',
        outcome,
        transactionId,
        attributes: {
            request_data: USER_PROFILE_REQUEST_DATA,
            ...(failureDetails.status_code !== undefined
                ? {
                    response_data: {
                        status_code: failureDetails.status_code,
                    },
                }
                : {}),
            transport_data: {
                failure_category: failureDetails.failure_category,
            },
        },
    });
};

/**
 * Fetch and normalize the authenticated user's initial profile.
 *
 * @param {Object} options
 * @param {string} options.transactionId - Request-local traceability ID
 * @returns {Promise<Object>}
 */
export const getUserPersonalInformationRequest = async ({
    transactionId,
}) => {
    const url = buildUrl(USER_PROFILE_ENDPOINT, {
        email: getUserEmail(),
    });

    logger.event(createUserProfileServiceRequestStartedEvent({
        transactionId,
    }));

    let response;
    try {
        response = await get(url);
    } catch (error) {
        logger.event(createUserProfileServiceRejectionEvent({
            transactionId,
            error,
        }));
        throw error;
    }

    logger.event(createUserProfileServiceResponseEvent({
        transactionId,
        rawResponse: response.data,
        statusCode: response.status,
    }));

    // Run the raw response through the single user normalizer so the rest
    // of the app never sees backend field names directly. See
    // src/data/User/user-normalizers.js for the canonical shape and the
    // rationale for boundary normalization.
    return normalizeUser(response.data);
};




export const updateUserRequest = async (data = {}) => {
    const url = '/users/user';

    try {
        const response = await put(url, data);

        return response;
    } catch(err) {
        console.error(err);
        throw err;
    }
};

export const changePasswordRequest = async (data = {}) => {
    const url = 'users/password';

    try {
        const response = await put(url, data);

        return response;
    } catch(err) {
        console.error(err);
        throw err;
    }
};
